import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Users, Store as StoreIcon, BadgeCheck, ShieldHalf, ShoppingBag, Package, ClipboardList,
  Star, LayoutList, Truck, Bike, Settings2, CreditCard, HandCoins, Banknote, QrCode, Send, Undo2, Handshake, Trash2, Plus,
  Percent, Crown, Megaphone, Flag, Scale, MessageSquareWarning, LifeBuoy, BarChart3, KeyRound, ScrollText, Settings, Gift,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { KpiCard } from '../../components/commerce/KpiCard';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS } from '../../data/mockData';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Stub, ArrowUpIcon, ArrowDownIcon } from './common';
import { ImageUpload } from '../../components/commerce/ImageUpload';
import { fileUrl } from '../../lib/upload';
import { IMAGES } from '../../data/mockData';

export function MarketingCampaigns() {
  const { campaign, updateCampaign, vouchers, createVoucher, toggleVoucher } = useApp();
  const [form, setForm] = useState(campaign);
  const [vform, setVform] = useState({ code: '', type: 'FIXED', value: '', minSpend: 0 });
  useEffect(() => setForm(campaign), [campaign]);
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const platformVouchers = vouchers.filter((v) => v.scope === 'PLATFORM');
  const submitVoucher = () => {
    createVoucher({ code: vform.code, type: vform.type, value: vform.value, minSpend: vform.minSpend, scope: 'PLATFORM' });
    setVform({ code: '', type: 'FIXED', value: '', minSpend: 0 });
  };
  return (
    <div data-testid="admin-marketing" className="max-w-2xl space-y-6">
      <div className="rounded-2xl bg-kelate-surface p-4 text-xs text-kelate-soft shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        Placement rule: only <b className="text-kelate-ink">BRAND (RM200)</b> merchants receive top featured placement on Home and Marketplace. LIST (RM10) stores show business info only — no products or promotions — until they upgrade.
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-kelate-ink">Homepage Campaign</h3>
          <label className="flex items-center gap-2 text-xs text-kelate-soft">
            Active
            <Switch data-testid="campaign-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          </label>
        </div>
        <div className="mt-4 space-y-3">
          <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Campaign title</label><input data-testid="campaign-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} /></div>
          <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Headline</label><input data-testid="campaign-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={input} /></div>
          <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Description</label><textarea data-testid="campaign-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood" /></div>
          <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Button text</label><input data-testid="campaign-cta" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className={input} /></div>
          <ImageUpload value={form.image || ''} onChange={(v) => setForm({ ...form, image: v })} label="Campaign banner image" testId="campaign-image-upload" aspect="banner" hint="Upload a banner image from your device (optional)" />
          <button data-testid="campaign-save" onClick={() => updateCampaign(form)} className="rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
            Save Campaign
          </button>
        </div>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Platform Vouchers</h3>
        <p className="mt-1 text-xs text-kelate-soft">KELATE-funded vouchers valid on every store. Buyers apply them at checkout alongside referral codes.</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-5">
          <input data-testid="av-code" placeholder="CODE (e.g. KELATE5)" value={vform.code} onChange={(e) => setVform({ ...vform, code: e.target.value.toUpperCase() })} className={input} />
          <select data-testid="av-type" value={vform.type} onChange={(e) => setVform({ ...vform, type: e.target.value })} className={input}>
            <option value="FIXED">RM off</option>
            <option value="PERCENT">% off</option>
          </select>
          <input data-testid="av-value" type="number" min="0" placeholder="Value" value={vform.value} onChange={(e) => setVform({ ...vform, value: e.target.value })} className={input} />
          <input data-testid="av-min-spend" type="number" min="0" placeholder="Min spend RM" value={vform.minSpend} onChange={(e) => setVform({ ...vform, minSpend: e.target.value })} className={input} />
          <button data-testid="av-create" onClick={submitVoucher} className="rounded-xl bg-kelate-darkwood text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Create</button>
        </div>
        <div className="mt-3 space-y-2">
          {platformVouchers.length === 0 && <p className="text-xs text-kelate-soft" data-testid="av-empty">No platform vouchers yet.</p>}
          {platformVouchers.map((v) => (
            <div key={v.code} data-testid={`av-row-${v.code}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
              <span className="font-mono font-bold text-kelate-darkwood">{v.code}</span>
              <span className="text-kelate-soft">{v.type === 'PERCENT' ? `${v.value}% off` : `${fmtRM2(v.value)} off`}{v.minSpend > 0 ? ` · min ${fmtRM2(v.minSpend)}` : ''}</span>
              <button data-testid={`av-toggle-${v.code}`} onClick={() => toggleVoucher(v.code)} className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${v.active ? 'bg-kelate-sage/15 text-kelate-sage' : 'bg-secondary text-kelate-soft'}`}>{v.active ? 'Active' : 'Off'}</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StaffManager() {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'rider' });
  const [error, setError] = useState('');
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const load = () => api.get('/admin/users').then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    setError('');
    if (!form.name || !form.email || !form.password) return setError('Name, email and password are required.');
    try {
      await api.post('/admin/users', form);
      toast.success(`${form.role === 'admin' ? 'Admin' : 'Rider'} account created`, { description: `${form.email} can now log in directly with the password you set.` });
      setForm({ name: '', email: '', password: '', role: 'rider' });
      load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const ROLE_STYLE = { admin: 'bg-kelate-darkwood text-kelate-surface', rider: 'bg-kelate-sage text-white', merchant: 'bg-kelate-wood text-white', consumer: 'bg-secondary text-kelate-ink' };
  return (
    <div data-testid="admin-staff" className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Staff Accounts</h2>
        <p className="text-xs text-kelate-soft">Create rider or admin accounts — they log in directly with the email and password you set. Roles: SUPER_ADMIN, OPERATIONS_ADMIN, FINANCE_ADMIN, MARKETING_ADMIN, SUPPORT_ADMIN, DELIVERY_ADMIN (mapped to admin/rider in this prototype).</p>
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Create Account</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input data-testid="staff-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          <input data-testid="staff-email" type="email" placeholder="Email (login ID)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
          <input data-testid="staff-password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
          <select data-testid="staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={input}>
            <option value="rider">Rider (Delivery Staff)</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {error && <p className="mt-2 text-xs text-kelate-red" data-testid="staff-error">{error}</p>}
        <button data-testid="staff-create" onClick={submit} className="mt-4 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
          Create Account
        </button>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">All Accounts</h3>
        <div className="space-y-2" data-testid="staff-list">
          {users === null && <p className="text-sm text-kelate-soft">Loading...</p>}
          {users?.map((u) => (
            <div key={u.id} data-testid={`staff-row-${u.email}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 px-4 py-2.5 text-sm">
              <div>
                <span className="font-medium text-kelate-ink">{u.name}</span>
                <span className="ml-2 text-xs text-kelate-soft">{u.email}</span>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ROLE_STYLE[u.role] || ROLE_STYLE.consumer}`}>{u.role}</span>
            </div>
          ))}
          {users?.length === 0 && <p className="text-sm text-kelate-soft">No accounts found.</p>}
        </div>
      </section>
    </div>
  );
}

export function PartnersAdmin() {
  const { partners, addPartner, removePartner } = useApp();
  const [form, setForm] = useState({ name: '', logo: '', profilePhoto: '', coverPhoto: '', description: '', website: '', facebook: '', instagram: '', tiktok: '' });
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const submit = () => {
    if (!form.name.trim()) return toast.error('Partner name is required');
    addPartner({
      name: form.name.trim(), logo: form.logo.trim(), profilePhoto: form.profilePhoto.trim(), coverPhoto: form.coverPhoto.trim(), description: form.description.trim(),
      socials: { website: form.website.trim(), facebook: form.facebook.trim(), instagram: form.instagram.trim(), tiktok: form.tiktok.trim() },
    });
    setForm({ name: '', logo: '', profilePhoto: '', coverPhoto: '', description: '', website: '', facebook: '', instagram: '', tiktok: '' });
  };
  return (
    <div data-testid="admin-partners" className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Collaboration Partners</h2>
        <p className="text-xs text-kelate-soft">Partner logos appear on the public /partners page. Customers tap a logo to view the partner's social media.</p>
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Add Partner</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input data-testid="partner-name" placeholder="Partner name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          <ImageUpload value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} label="Logo" testId="partner-logo-upload" aspect="avatar" />
          <ImageUpload value={form.profilePhoto} onChange={(v) => setForm({ ...form, profilePhoto: v })} label="Profile photo (1080x1080)" testId="partner-profile-upload" aspect="avatar" />
          <ImageUpload value={form.coverPhoto} onChange={(v) => setForm({ ...form, coverPhoto: v })} label="Cover photo (1920x1080)" testId="partner-cover-upload" aspect="cover" />
          <input data-testid="partner-description" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} sm:col-span-2`} />
          <input data-testid="partner-website" placeholder="Website URL" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={input} />
          <input data-testid="partner-facebook" placeholder="Facebook URL" value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} className={input} />
          <input data-testid="partner-instagram" placeholder="Instagram URL" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={input} />
          <input data-testid="partner-tiktok" placeholder="TikTok URL" value={form.tiktok} onChange={(e) => setForm({ ...form, tiktok: e.target.value })} className={input} />
        </div>
        <button data-testid="partner-add" onClick={submit} className="mt-4 flex items-center gap-1.5 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
          <Plus size={14} /> Add Partner
        </button>
      </section>
      <section className="space-y-2">
        {partners.map((p) => (
          <div key={p.id} data-testid={`admin-partner-${p.id}`} className="flex items-center gap-3 rounded-2xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            {p.profilePhoto || p.logo ? (
              <img src={fileUrl(p.profilePhoto || p.logo)} alt="" className="h-11 w-11 rounded-xl object-cover" />
            ) : (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-kelate-darkwood font-display text-lg font-semibold text-kelate-surface">{p.name[0]}</span>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-kelate-ink">{p.name}</div>
              <div className="truncate text-xs text-kelate-soft">{p.description || 'No description'}</div>
            </div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-kelate-soft">
              {Object.entries(p.socials || {}).filter(([, v]) => v).map(([k]) => <span key={k} className="rounded-full bg-secondary px-2 py-0.5">{k}</span>)}
            </div>
            <button data-testid={`partner-remove-${p.id}`} onClick={() => removePartner(p.id)} className="rounded-xl p-2 text-kelate-soft hover:bg-kelate-red/10 hover:text-kelate-red" title="Remove partner">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {partners.length === 0 && <EmptyState icon={Handshake} title="No partners yet" description="Add your first collaboration partner above." />}
      </section>
    </div>
  );
}

const MIK_DEFAULT = {
  hero: { kicker: 'Featured Brand Story', title: 'Made in Kelantan', subtitle: "From hand-drawn batik in Kampung Kraftangan to keropok ikan from Bachok — every purchase supports a Kelantan maker.", image: '' },
  heroImage: '',
  stories: [
    { kicker: 'Craft', title: 'Batik, block by block', body: 'Hand-drawn and block-printed in Kampung Kraftangan, Kelantan batik carries motifs passed down for generations.', image: '' },
    { kicker: 'Food', title: 'From Bachok waters to your table', body: 'Keropok ikan, nasi kerabu and akok made the way mak cik has always made them.', image: '' },
    { kicker: 'Fashion', title: 'Songket meets modern modest wear', body: 'Kelantanese weavers and young designers are reimagining songket and batik for everyday wear.', image: '' },
  ],
};

const HERO_DEFAULT = {
  kicker: "Kelantan's own marketplace",
  title: 'Discover Kelantan, all in one place.',
  subtitle: 'Shop local products, discover trusted businesses and support Kelantan brands.',
  image: '',
  cardImage: '',
  cardText: 'Local favourites from Kelantan',
};

export function HomepageHeroCms() {
  const { homeHero, updateHomeHero } = useApp();
  const [form, setForm] = useState(homeHero || HERO_DEFAULT);
  useEffect(() => setForm(homeHero || HERO_DEFAULT), [homeHero]);
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  return (
    <div data-testid="admin-homepage-hero" className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Homepage Hero</h2>
        <p className="text-xs text-kelate-soft">Edit the title, text and photos on the first page of the website. Changes go live after saving.</p>
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Title & Text</h3>
        <div className="grid gap-3">
          <input data-testid="hero-kicker-input" placeholder="Kicker (small text above title)" value={form.kicker || ''} onChange={(e) => setForm({ ...form, kicker: e.target.value })} className={input} />
          <input data-testid="hero-title-input" placeholder="Main title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} />
          <textarea data-testid="hero-subtitle-input" placeholder="Subtitle text" rows={2} value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood" />
        </div>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Photos</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <ImageUpload value={form.image || ''} onChange={(v) => setForm({ ...form, image: v })} label="Main hero photo" testId="hero-image-upload" aspect="banner" hint="Large photo on the right of the first page" />
          <ImageUpload value={form.cardImage || ''} onChange={(v) => setForm({ ...form, cardImage: v })} label="Small card photo" testId="hero-card-image-upload" aspect="square" hint="Small floating card photo (bottom-left)" />
        </div>
        <div className="mt-3">
          <input data-testid="hero-card-text-input" placeholder="Small card text" value={form.cardText || ''} onChange={(e) => setForm({ ...form, cardText: e.target.value })} className={input} />
        </div>
        <button data-testid="hero-save" onClick={() => updateHomeHero(form)} className="mt-4 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Save Hero</button>
      </section>
    </div>
  );
}

export function MadeInKelantanCms() {
  const { madeInKelantan, updateMadeInKelantan } = useApp();
  const [cfg, setCfg] = useState(madeInKelantan || MIK_DEFAULT);
  useEffect(() => setCfg(madeInKelantan || MIK_DEFAULT), [madeInKelantan]);
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const setStory = (i, patch) => {
    const stories = cfg.stories.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setCfg({ ...cfg, stories });
  };
  return (
    <div data-testid="admin-made-in-kelantan" className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Made in Kelantan Page</h2>
        <p className="text-xs text-kelate-soft">Edit the hero and story sections of the public Made in Kelantan page. Upload photos from your device.</p>
      </div>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Hero Section</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input data-testid="mik-hero-kicker" placeholder="Kicker" value={cfg.hero?.kicker || ''} onChange={(e) => setCfg({ ...cfg, hero: { ...(cfg.hero || {}), kicker: e.target.value } })} className={input} />
          <input data-testid="mik-hero-title" placeholder="Title" value={cfg.hero?.title || ''} onChange={(e) => setCfg({ ...cfg, hero: { ...(cfg.hero || {}), title: e.target.value } })} className={input} />
          <textarea data-testid="mik-hero-subtitle" placeholder="Subtitle" rows={2} value={cfg.hero?.subtitle || ''} onChange={(e) => setCfg({ ...cfg, hero: { ...(cfg.hero || {}), subtitle: e.target.value } })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood sm:col-span-2" />
        </div>
        <div className="mt-3">
          <ImageUpload value={cfg.hero?.image || cfg.heroImage || ''} onChange={(v) => setCfg({ ...cfg, hero: { ...(cfg.hero || {}), image: v }, heroImage: v })} label="Hero image" testId="mik-hero-upload" aspect="banner" hint="Wide image shown at the top of the page" />
        </div>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Story Sections</h3>
        <div className="space-y-5">
          {(cfg.stories || []).map((s, i) => (
            <div key={i} data-testid={`mik-story-editor-${i}`} className="rounded-2xl border border-kelate-line/60 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid={`mik-story-kicker-${i}`} placeholder="Kicker (e.g. Craft)" value={s.kicker} onChange={(e) => setStory(i, { kicker: e.target.value })} className={input} />
                <input data-testid={`mik-story-title-${i}`} placeholder="Title" value={s.title} onChange={(e) => setStory(i, { title: e.target.value })} className={input} />
                <textarea data-testid={`mik-story-body-${i}`} placeholder="Body text" rows={3} value={s.body} onChange={(e) => setStory(i, { body: e.target.value })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood sm:col-span-2" />
                <div className="sm:col-span-2">
                  <ImageUpload value={s.image || ''} onChange={(v) => setStory(i, { image: v })} label="Story photo" testId={`mik-story-image-${i}`} aspect="cover" hint="Upload from device (leave blank to use the default photo)" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="mik-save" onClick={() => updateMadeInKelantan(cfg)} className="mt-4 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Save Page</button>
      </section>
    </div>
  );
}

export function HomepageCms() {
  const { homeLayout, updateHomeLayout, customProducts, customMerchants, partners, campaign } = useApp();
  const [layout, setLayout] = useState(homeLayout);
  useEffect(() => setLayout(homeLayout), [homeLayout]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= layout.length || layout[i].locked || layout[j].locked) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    setLayout(next);
  };
  const toggle = (i) => {
    if (layout[i].locked) return;
    setLayout(layout.map((s, idx) => (idx === i ? { ...s, enabled: !s.enabled } : s)));
  };
  const previewMeta = {
    hero: 'Headline, search bar, hero image', categories: '10 category chips', deals: `${customProducts.filter((p) => (p.badges || []).includes('SALE')).length} sale products`,
    campaign: campaign.active ? `Active: ${campaign.title}` : 'Currently inactive', madeInKelantan: 'Local products rail', nearYou: 'Location-based products',
    featuredStores: `${customMerchants.length} stores`, bestSelling: 'Top sellers rail', newArrivals: 'New products rail',
    explore: '10 districts grid', partners: `${partners.length} partners`, merchantCta: 'Plan pricing + CTA',
  };
  return (
    <div data-testid="admin-homepage" className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-kelate-ink">Homepage Layout</h2>
        <p className="text-xs text-kelate-soft">Reorder or hide homepage sections. Changes go live immediately after saving.</p>
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <section className="space-y-2">
          {layout.map((s, i) => (
            <div key={s.id} data-testid={`home-section-${s.id}`} className={`flex items-center gap-2 rounded-2xl bg-kelate-surface p-3 shadow-[0_1px_4px_rgba(81,68,55,0.08)] ${!s.enabled ? 'opacity-60' : ''}`}>
              <div className="flex flex-col">
                <button data-testid={`section-up-${s.id}`} onClick={() => move(i, -1)} disabled={i === 0 || s.locked || layout[i - 1]?.locked} className="px-1 text-kelate-soft hover:text-kelate-darkwood disabled:opacity-30"><ArrowUpIcon /></button>
                <button data-testid={`section-down-${s.id}`} onClick={() => move(i, 1)} disabled={i === layout.length - 1 || s.locked || layout[i + 1]?.locked} className="px-1 text-kelate-soft hover:text-kelate-darkwood disabled:opacity-30"><ArrowDownIcon /></button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-kelate-ink">{s.label} {s.locked && <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-kelate-soft">Pinned</span>}</div>
                <div className="truncate text-[11px] text-kelate-soft">{previewMeta[s.id]}</div>
              </div>
              <Switch data-testid={`section-toggle-${s.id}`} checked={s.enabled} disabled={!!s.locked} onCheckedChange={() => toggle(i)} />
            </div>
          ))}
          <button data-testid="homepage-save" onClick={() => updateHomeLayout(layout)} className="mt-2 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
            Save Layout
          </button>
        </section>
        <section className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid="homepage-preview">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-kelate-soft">Live Preview (section order)</h3>
          <div className="space-y-1.5">
            {layout.filter((s) => s.enabled).map((s, i) => (
              <div key={s.id} data-testid={`preview-${s.id}`} className="flex items-center gap-2.5 rounded-xl border border-kelate-line/60 bg-kelate-bg px-3 py-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-kelate-darkwood text-[10px] font-bold text-kelate-surface">{i + 1}</span>
                <span className="text-xs font-medium text-kelate-ink">{s.label}</span>
                <span className="ml-auto truncate text-[10px] text-kelate-soft">{previewMeta[s.id]}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

